import { Fragment } from 'react';
import { Box, Stack, Typography as MuiTypography, Avatar, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import Text from '../../../../components/detail/components/Text.component';
import RectangleChip from '../../../../components/common/RectangleChip.component';
import { grey } from '@mui/material/colors';
import { departmentIcons, departmentColors } from '../Data.conf';

const PRIORITY_COLORS = {
    URGENT: '#d32f2f',
    MEDIUM: '#f5b400',
    LOW: '#388e3c',
};

const STATUS_COLORS = {
    OPEN: '#d32f2f',
    CLOSED: '#388e3c',
};

export const DetailConf = {
    initialState: {
        loading: false,
        error: null,
        data: null,
        meta: null,
    },
    fields: [
        {
            type: 'text',
            label: '',
            key: 'attributes.department',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.priority',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.status',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.type',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.yacht_name',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.team',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.assignee_user',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.createdAt',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.created_by_user',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.closed_by_user',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.updatedAt',
            xs: 12,
            sm: 6,
            lg: 4,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.handover',
            xs: 12,
        },
        {
            type: 'text',
            label: '',
            key: 'attributes.notes',
            xs: 12,
        },
    ],
    formatValue: (key, value) => {
        switch (key) {
            case 'attributes.yacht_name':
                return <Text label='Yacht' value={value || '-'} sx={{ mb: 1 }} />;

            case 'attributes.type':
                return value ? (
                    <Box sx={{ mb: 1 }}>
                        <RectangleChip
                            value={value.toUpperCase()}
                            backgroundColor='#009ab6'
                            color='#fff'
                            width='auto'
                        />
                    </Box>
                ) : (
                    <Text label='Type' value='-' sx={{ mb: 1 }} />
                );

            case 'attributes.priority':
                return value ? (
                    <Box sx={{ mb: 1 }}>
                        <RectangleChip
                            value={value}
                            backgroundColor={PRIORITY_COLORS[value] || '#757575'}
                            color='#fff'
                        />
                    </Box>
                ) : (
                    <Text label='Priority' value='-' sx={{ mb: 1 }} />
                );

            case 'attributes.status':
                return value ? (
                    <Box sx={{ mb: 1 }}>
                        <RectangleChip
                            value={value}
                            backgroundColor={STATUS_COLORS[value] || '#757575'}
                            color='#fff'
                        />
                    </Box>
                ) : (
                    <Text label='Status' value='-' sx={{ mb: 1 }} />
                );

            case 'attributes.department': {
                const backgroundColor = departmentColors?.[value] || '#8CB9D4';
                const icon = departmentIcons?.[value] || value?.charAt?.(0) || '-';

                return value && value !== '-' ? (
                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={value}>
                            <Avatar
                                sx={{
                                    bgcolor: backgroundColor,
                                    color: 'white',
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.95rem',
                                    fontWeight: 'bold',
                                }}
                            >
                                {icon}
                            </Avatar>
                        </Tooltip>
                    </Box>
                ) : (
                    <Text label='Department' value='-' />
                );
            }

            case 'attributes.team':
                return <Text label='Team' value={value || '-'} sx={{ mb: 1 }} />;

            case 'attributes.assignee_user':
                return <Text label='Assignee' value={value || '-'} sx={{ mb: 1 }} />;

            case 'attributes.created_by_user':
                return <Text label='Created by' value={value || '-'} sx={{ mb: 1 }} />;

            case 'attributes.closed_by_user':
                return <Text label='Closed by' value={value || '-'} sx={{ mb: 1 }} />;

            case 'attributes.createdAt':
                return (
                    <Text
                        label='Created'
                        sx={{ mb: 1 }}
                        value={
                            value ? (
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <CalendarIcon sx={{ fontSize: 16 }} />
                                    <MuiTypography variant='body2'>
                                        {dayjs(value).format('DD/MM/YYYY HH:mm')}
                                    </MuiTypography>
                                </Stack>
                            ) : (
                                <MuiTypography variant='body2'>-</MuiTypography>
                            )
                        }
                    />
                );
            case 'attributes.updatedAt':
                return (
                    <Text
                        label='Updated'
                        sx={{ mb: 1 }}
                        value={
                            value ? (
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <CalendarIcon sx={{ fontSize: 16 }} />
                                    <MuiTypography variant='body2'>
                                        {dayjs(value).format('DD/MM/YYYY HH:mm')}
                                    </MuiTypography>
                                </Stack>
                            ) : (
                                <MuiTypography variant='body2'>-</MuiTypography>
                            )
                        }
                    />
                );
            case 'attributes.handover': {
                const title = value?.title || '-';
                const description = value?.description || '-';

                return (
                    <Box sx={{ width: '100%', mb: 1 }}>
                        <Text label='Handover' value='' sx={{ mb: 1 }} />

                        <Box>
                            <MuiTypography
                                variant='body2'
                                sx={{
                                    fontWeight: 700,
                                    mb: 0.5,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {title}
                            </MuiTypography>

                            <MuiTypography
                                variant='body2'
                                sx={{
                                    fontWeight: 500,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {description}
                            </MuiTypography>
                        </Box>
                    </Box>
                );
            }
            case 'attributes.notes': {
                const notes = value || [];
                const previewNotes = notes.slice(-3).reverse();
                const hiddenCount = notes.length - 3;

                if (!notes.length) {
                    return <Text label='Update' value='-' sx={{ mb: 1 }} />;
                }

                return (
                    <Box sx={{ width: '100%' }}>
                        <Text label='Update' value='' sx={{ mb: 1 }} />

                        <Fragment>
                            <Stack spacing={1} sx={{ maxWidth: '100%' }}>
                                {previewNotes.map((note, index) => (
                                    <Box
                                        key={`${note?.created_at_note || 'note'}-${index}`}
                                        sx={{
                                            pb: 0.75,
                                            borderBottom:
                                                index < previewNotes.length - 1
                                                    ? '1px solid #e0e0e0'
                                                    : 'none',
                                        }}
                                    >
                                        <MuiTypography
                                            variant='body2'
                                            sx={{
                                                fontWeight: 500,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {note?.comment || '-'}
                                        </MuiTypography>

                                        <MuiTypography
                                            variant='caption'
                                            sx={{
                                                color: 'text.secondary',
                                                display: 'block',
                                            }}
                                        >
                                            {note?.created_at_note
                                                ? `${dayjs(note.created_at_note).format('DD/MM/YYYY HH:mm')} - ${note?.user_name || '-'}`
                                                : `- ${note?.user_name || '-'}`
                                            }
                                        </MuiTypography>
                                    </Box>
                                ))}
                            </Stack>

                            {notes.length > 3 && (
                                <Box pt={1}>
                                    <Tooltip title={`${hiddenCount} more update${hiddenCount > 1 ? 's' : ''}`}>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 10,
                                                backgroundColor: grey[200],
                                                color: 'text.secondary',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            +{hiddenCount}
                                        </Box>
                                    </Tooltip>
                                </Box>
                            )}
                        </Fragment>
                    </Box>
                );
            }

            default:
                return null;
        }
    },
};